import { useDebouncedValue } from '@mantine/hooks'
import { APP_BANNER } from '@pubstack/common/src/adFormat'
import { AdUnitEnv, buildPbsAdUnitsExportCSV, PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { parseAsInteger, parseAsNumberLiteral, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { FunctionComponent, MouseEvent, useCallback, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { fetchAdUnitsForExport, useAdUnitsStacks, useAdUnitsWithPagination } from '~/api/pbs/ad-units.api'
import { PAGE_LIMIT_OPTIONS, Pagination } from '~/components/Pagination'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { Popover, PopoverContentWrapper, PopoverLine } from '~/components/Popover'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Tooltip } from '~/components/Tooltip'
import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import { AdFormatIcon } from '~/modules/adm/inventory/adunits/AdFormatIcon'
import { PureUsedStackTooltip } from '~/modules/adm/rules/PureUsedStackTooltip'
import TableRow, { ROW_CELL_CLASSES } from '~/components/table/TableRow'
import { AD_UNIT_TABS } from '~/modules/monetization/inventory/adunits/adUnitTab'
import { ImportAdUnitsCsvFlyout } from '~/modules/pbs/adunits/ImportAdUnitsCsvFlyout'
import { ArchiveAdUnitModal } from './ArchiveAdUnitModal'
import { CreateAdUnitsFlyout } from './CreateAdUnitsFlyout'
import { ViewAdUnitFlyout } from './ViewAdUnitFlyout'
import { downloadCSVData } from '~/utils/fileDownload'

/** The row above the table: search on one side, the buttons on the other. */
const TOP_ACTIONS_CLASSES = 'flex w-full items-center justify-between'

/** Cells stay narrow enough that a long ad unit name cannot push the actions off the row. */

/**
 * The width lives on the wrapper, not on the input. The top actions row spreads its children apart, so this box is
 * only ever as wide as its content, and an input left to size itself collapses to its placeholder. Same
 * value as the Web tab: these are tabs of one page and their toolbars sit in the same place.
 */
const SEARCH_FORM_CLASSES = 'my-3 flex w-2/5 max-w-[592px] shrink-0 gap-3 [&>*]:w-full'

/** The table and its pager as one column, spaced like the medias list rather than sitting flush together. */
const TABLE_WITH_PAGINATION_CLASSES = 'flex flex-col gap-4'

// Constants
const FLYOUT_WIDTH = '600px'
const SEARCH_DEBOUNCE_TIME_MILLIS = 300
const ITEMS_PER_PAGE = 25
/** The one attribute the server can order the list by, so the header only flips the direction. */
const SORT_BY = 'name'

// Tracking labels
const CREATE_TRACKING_LABEL = 'pbs-ad-units-new'
const VIEW_TRACKING_LABEL = 'pbs-ad-units-view'
const ARCHIVE_TRACKING_LABEL = 'pbs-ad-units-archive'
// `-upload` / `-download`, matching the web tab's own import and export buttons: TrackingLabel's
// action suffix is a closed set, and the two tabs' equivalent buttons should report alike.
const IMPORT_TRACKING_LABEL = 'pbs-ad-units-upload'
const EXPORT_TRACKING_LABEL = 'pbs-ad-units-download'

/**
 * Named for the tab, not for the environment. Inside Node's own vocabulary `s2s` is the web
 * environment as opposed to in-app and AMP, which is why "Web ad units" survived — but the tab holding
 * it is labelled `Server-side` and its neighbour is labelled `Web`, so the old string named the tab
 * next door, and on a Node-only scope it named a product the reader does not own.
 */
const adunitsTitleByEnv = {
  s2s: 'Server-side ad units',
  inapp: 'In-app ad units',
  amp: 'AMP ad units',
}

type adUnitsPageProps = {
  env: Exclude<AdUnitEnv, 'web'>
}

export const AdUnitsPage: FunctionComponent<adUnitsPageProps> = ({ env }) => {
  // State
  const [actionsOpen, setActionsOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  // State — the page window and the ordering live in the URL, so a list someone is looking at is a link
  // they can send. `search` joins them below, once the input has settled.
  const [page, setPage] = useQueryState<number>('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: false }))
  const [limit, setLimit] = useQueryState<number>('limit', parseAsNumberLiteral(PAGE_LIMIT_OPTIONS).withDefault(ITEMS_PER_PAGE).withOptions({ clearOnDefault: false }))
  const [urlSearch, setUrlSearch] = useQueryState<string>('search', parseAsString.withDefault(''))
  const [sortOrder, setSortOrder] = useQueryState<'ascending' | 'descending'>(
    'sortOrder',
    parseAsStringLiteral(['ascending', 'descending']).withDefault('ascending').withOptions({ clearOnDefault: false })
  )
  const columns: TableColumns<PbsAdUnit> = [
    {
      name: 'Ad unit',
      isSortable: true,
      order: sortOrder,
      attributeSort: SORT_BY,
    },
    {
      name: 'Used by',
      isSortable: false,
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ]

  // API Hooks
  const { data: stacksByAdUnit } = useAdUnitsStacks()
  const {
    data: { data: adUnits, pagination: adUnitsPagination },
    isLoading,
  } = useAdUnitsWithPagination({
    // The archived ones live on their own page, so this list asks for the live ones and never has to
    // filter them back out.
    filters: { search: urlSearch || undefined, env },
    pagination: { page, limit },
    sortBy: SORT_BY,
    sortOrder,
  })
  const navigate = useNavigate()
  const { canArchive, canCreate, disabledReason } = useScopePermissions()
  // Read off the tab map rather than spelled again here: the env→URL mapping has one home.
  const archivedPath = AD_UNIT_TABS.find((tab) => tab.env === env)?.path

  // Hooks
  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()

  // Form hooks
  const { control } = useForm<{
    search: string
  }>({
    // Seeded from the URL so a shared link opens on the same search the sender was looking at.
    defaultValues: {
      search: urlSearch,
    },
  })
  const search = useWatch({ control, name: 'search' })
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_TIME_MILLIS)

  // The settled search is what reaches the URL, and through it the request. Typing moves the reader back to
  // the first page: page 4 of the previous search is not a place they asked to be.
  useEffect(() => {
    if (debouncedSearch === urlSearch) return
    setUrlSearch(debouncedSearch)
    setPage(1)
  }, [debouncedSearch])

  const handleNewClick = () => {
    if (env === 's2s') {
      flyout.open(
        CreateAdUnitsFlyout,
        {
          color: Colors.Kaiminus,
          width: FLYOUT_WIDTH,
          closeTrackingLabel: `${CREATE_TRACKING_LABEL}-close`,
        },
        { onClose: () => flyout.close() }
      )
    } else {
      navigate(`/monetization/node/adunits/new/${env}`)
    }
  }

  const handleImportClick = () => {
    flyout.open(
      ImportAdUnitsCsvFlyout,
      {
        color: Colors.Kaiminus,
        width: FLYOUT_WIDTH,
        closeTrackingLabel: `${CREATE_TRACKING_LABEL}-close`,
      },
      { onClose: () => flyout.close(), env }
    )
  }

  const handleViewClick = useCallback(
    (event: MouseEvent<HTMLElement>, adUnit: PbsAdUnit) => {
      event.preventDefault()
      event.stopPropagation()
      // As wide as every flyout carrying a "Used by" table.
      flyout.open(ViewAdUnitFlyout, { color: Colors.Ming, width: '816px', closeTrackingLabel: `${VIEW_TRACKING_LABEL}-close` }, { adUnit })
    },
    [flyout]
  )

  const handleArchive = useCallback(
    (event: MouseEvent<HTMLElement>, adUnit: PbsAdUnit) => {
      event.preventDefault()
      event.stopPropagation()
      modal.open(ArchiveAdUnitModal, { adUnit })
    },
    [modal]
  )

  /* Exports what the tab is showing, filtered and sorted as the reader left it, rather than the raw
     collection: the file answers the question on screen. Now that the table shows one page, that has to
     mean every row those filters select — so the rows are fetched for the file rather than taken from the
     page, which would hand back ten of a thousand. Archived ad units stay out — they are on their own
     page, and an export that re-imported them would resurrect what was deliberately retired. */
  const handleExportClick = async () => {
    setIsExporting(true)
    try {
      const rows = await fetchAdUnitsForExport({ search: urlSearch || undefined, env }, SORT_BY, sortOrder)
      downloadCSVData(buildPbsAdUnitsExportCSV(rows, env), `adunits-${env}-export.csv`)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      {/* A search that matches nothing is still a populated tab: it gets the table and its "No results" row,
          not the empty-inventory illustration, which would read as "your ad units are gone". */}
      {isLoading || adUnitsPagination.total > 0 || !!urlSearch ? (
        <>
          <div className={TOP_ACTIONS_CLASSES}>
            <div className={SEARCH_FORM_CLASSES}>
              <Input name="search" type="text" iconLeft="search" labelIsPlaceholder label="Search" control={control} />
            </div>
            <div className={'flex items-center gap-3'}>
              {/* Adding an ad unit is the common act and keeps its own button. Export, import and the archive
                  are the occasional ones, and three more buttons beside it made the row read as four equal
                  choices — so they sit behind the kebab, the same table-level menu the media list uses.
                  Help moved up to the page title, where a question about the page belongs. */}
              {/* Creating an ad unit is an owner's, like creating the media it will run on: it is inventory the
                  whole scope deploys against. The button stays on screen saying why — seeing that the
                  capability exists is half of knowing to ask an owner for it. */}
              <Tooltip title={disabledReason} disabled={canCreate}>
                <Button iconName="add_adUnit" variant="primary" disabled={!canCreate} onClick={handleNewClick} trackingLabel={CREATE_TRACKING_LABEL}>
                  {adunitsTitleByEnv[env]}
                </Button>
              </Tooltip>
              <Popover
                align={'end'}
                open={actionsOpen}
                setOpen={setActionsOpen}
                trigger={<Button variant={'tertiary'} iconName={'more_vert'} title={'More actions'} trackingLabel={'pbs-ad-units-actions-view'} />}
              >
                <PopoverContentWrapper>
                  <PopoverLine
                    onClick={() => {
                      setActionsOpen(false)
                      handleExportClick()
                    }}
                    data-tracking-label={EXPORT_TRACKING_LABEL}
                  >
                    <Icon width={'20px'} name={'download'} />
                    <span>{isExporting ? 'Exporting…' : 'Export ad units'}</span>
                  </PopoverLine>
                  {/* Same owner gate as the import button in the empty state below, and for the same reason the
                      archive entry has one: the bulk CSV endpoint refuses a non-owner, so an entry everyone can
                      see would only ever lead to a 403 — after they had already picked a file. */}
                  {canCreate && (
                    <PopoverLine
                      onClick={() => {
                        setActionsOpen(false)
                        handleImportClick()
                      }}
                      data-tracking-label={IMPORT_TRACKING_LABEL}
                    >
                      <Icon width={'20px'} name={'upload'} />
                      <span>Import ad units</span>
                    </PopoverLine>
                  )}
                  {/* The only way to the archived ad units. Owners only, because the page itself is
                      owner-gated — an entry everyone can see leading to a 403 is worse than no entry. */}
                  {canArchive && archivedPath && (
                    <PopoverLine
                      onClick={() => {
                        setActionsOpen(false)
                        navigate(`/monetization/inventory/adunits/${archivedPath}/archived`)
                      }}
                      data-tracking-label={'pbs-ad-units-archived-navigate'}
                    >
                      <Icon width={'20px'} name={'archive'} />
                      <span>Archived ad units</span>
                    </PopoverLine>
                  )}
                </PopoverContentWrapper>
              </Popover>
            </div>
          </div>

          {/* Column with a gap rather than the table and the pager touching: same spacing the medias list uses. */}
          <div className={TABLE_WITH_PAGINATION_CLASSES}>
            <Table
              columns={columns}
              isLoading={isLoading}
              onClickHeading={() => {
                setSortOrder(sortOrder === 'ascending' ? 'descending' : 'ascending')
                setPage(1)
              }}
            >
              {adUnits.length > 0 ? (
                adUnits.map((adUnit) => (
                  <TableRow
                    key={adUnit.id}
                    className={ROW_CELL_CLASSES}
                    clickable={{
                      onClick: (e) => handleViewClick(e, adUnit),
                      trackingLabel: VIEW_TRACKING_LABEL,
                    }}
                  >
                    <TableCell>
                      <span className={'flex items-center gap-2'}>
                        {/* A server-side ad unit carries no ad format of its own, so the slot every other env
                          fills beside the name was empty on this tab alone. The env serves banner inventory and
                          nothing else, so the row is drawn with the format actually named `Banner` — the same
                          one, through the same component, that the in-app and AMP tabs already draw. */}
                        <AdFormatIcon adFormatId={env === 's2s' ? APP_BANNER.id : adUnit.adFormat?.id} />
                        <span>{adUnit.name}</span>
                      </span>
                    </TableCell>
                    <TableCell>{stacksByAdUnit[adUnit.id] ? <PureUsedStackTooltip stacksBySite={stacksByAdUnit[adUnit.id]} /> : <span>0 stack</span>}</TableCell>
                    <ActionsTableCell>
                      <ActionButtons>
                        {/* No pencil here: the rename hangs off the name itself, from the pen beside it in the
                          View flyout — which is this env's form. */}
                        {/* No settings form on any node env. In-app was the one that still offered one, which let
                          the ad format of a live ad unit be changed after the fact — the one field the served
                          inventory is keyed on. Server-side and AMP never had the route; in-app now matches
                          them, and View is the read-only surface for all three. */}
                        <Tooltip title="View">
                          <Button iconName="view" variant="tertiary" title="View" onClick={(e) => handleViewClick(e, adUnit)} trackingLabel={VIEW_TRACKING_LABEL} />
                        </Tooltip>
                        {/* Archiving is the owner's call and the API says so too, so a user below owner is not
                          offered a button that would come back a 403. */}
                        {canArchive && (
                          <Tooltip title="Archive">
                            <Button iconName="archive" variant="tertiary" title="Archive" onClick={(e) => handleArchive(e, adUnit)} trackingLabel={ARCHIVE_TRACKING_LABEL} />
                          </Tooltip>
                        )}
                      </ActionButtons>
                    </ActionsTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <NoResultsTableCell colspan={columns.length}>No results</NoResultsTableCell>
                </TableRow>
              )}
            </Table>

            <Pagination
              currentPage={page}
              totalPages={adUnitsPagination.totalPages}
              limit={limit}
              totalItems={adUnitsPagination.total}
              onPageChange={(p) => setPage(p)}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          </div>
        </>
      ) : (
        <IllustrationMessage
          iconName="night_sky"
          iconSize="190px"
          title="It seems that no ad unit has been connected to your inventory."
          message="Add your ad units to the inventory so you can monetise them through Pubstack."
        >
          {/* No Export here: there is nothing yet to export, and offering it on an empty inventory would
              hand back an empty file. */}
          {/* Importing is creating in bulk, so it answers to the same gate as the button beside it. */}
          <div className={'flex items-center gap-3'}>
            <Tooltip title={disabledReason} disabled={canCreate}>
              <Button variant={'secondary'} iconName={'upload'} disabled={!canCreate} onClick={handleImportClick} trackingLabel={IMPORT_TRACKING_LABEL}>
                Import ad units
              </Button>
            </Tooltip>
            <Tooltip title={disabledReason} disabled={canCreate}>
              <Button iconName="add_adUnit" variant="primary" disabled={!canCreate} onClick={handleNewClick} trackingLabel={CREATE_TRACKING_LABEL}>
                {adunitsTitleByEnv[env]}
              </Button>
            </Tooltip>
          </div>
        </IllustrationMessage>
      )}
    </>
  )
}
